/**
 * Apertura de caja.
 *
 * El recepcionista que recibe el turno cuenta la gaveta y declara lo que hay,
 * por moneda. Ese fondo es lo que después permite que el arqueo compare bien:
 * sin él, el conteo físico se comparaba solo contra lo cobrado y el fondo
 * aparecía como sobrante en todos los turnos.
 *
 * Los campos arrancan VACÍOS a propósito. Un cero escrito ("conté y no había")
 * dice algo distinto a un campo que nadie miró, y la única forma de que el dato
 * signifique algo es que alguien cuente antes de abrir.
 *
 * Las monedas y las tasas las dice el servidor (`/opening-sheet`), así que si
 * el hotel agrega una divisa el formulario la incluye sin tocar esta pantalla.
 */

import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { postApi, getApi } from 'views/services/api';

const CURRENCY_SYMBOLS = { USD: '$', VES: 'Bs.', COP: '$', MXN: '$', EUR: '€' };

const money = (amount, currency) =>
  `${CURRENCY_SYMBOLS[currency] || currency} ${Number(amount || 0).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const numeric = { fontVariantNumeric: 'tabular-nums' };

const OpenCashRegister = () => {
  const theme = useTheme();
  const [sheet, setSheet] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [openRegister, setOpenRegister] = useState(null);

  const loadSheet = async () => {
    try {
      const r = await getApi('api/cashregister/opening-sheet');
      if (r?.data) {
        setSheet(r.data);
        setOpenRegister(r.data.isOpen ? r.data.openRegister : null);
        if (r.data.isOpen && r.data.openRegister) {
          localStorage.setItem('currentCashRegisterNumber', r.data.openRegister.registerNumber);
        }
      }
    } catch (error) {
      console.error('Error al preparar la apertura:', error);
      toast.error('No se pudo cargar la configuración de monedas del hotel.');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    loadSheet();
  }, []);

  const currencies = sheet?.currencies || [];
  const baseCurrency = sheet?.baseCurrency || 'USD';

  /** Total del fondo en moneda base, en vivo. */
  const totalInBase = currencies.reduce((acc, option) => {
    const value = Number(amounts[option.currency]);
    if (!Number.isFinite(value)) return acc;
    return acc + (option.isBase ? value : value / (option.rate || 1));
  }, 0);

  const allDeclared = currencies.every(
    (option) => amounts[option.currency] !== undefined && amounts[option.currency] !== ''
  );

  const handleOpen = async () => {
    setLoading(true);
    try {
      const payload = {
        openingFloat: currencies.map((option) => ({
          currency: option.currency,
          amount: Number(amounts[option.currency] || 0),
          exchangeRateUsed: option.rate
        }))
      };

      const response = await postApi('api/cashregister/open', payload);

      if (response?.status === 200) {
        const { cashRegister } = response.data;
        localStorage.setItem('currentCashRegisterNumber', cashRegister.registerNumber);
        toast.success(`Caja #${cashRegister.registerNumber} abierta`);
        setOpenRegister(cashRegister);
      } else {
        toast.error(response?.response?.data?.error || 'No se pudo abrir la caja.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'No se pudo abrir la caja.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={8}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          Verificando el estado de la caja…
        </Typography>
      </Box>
    );
  }

  // ── Ya hay una caja abierta ─────────────────────────────────────────
  if (openRegister) {
    const tone = theme.palette.success.dark;
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: alpha(tone, 0.4), bgcolor: alpha(tone, 0.03) }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
            <CheckCircleIcon fontSize="small" sx={{ color: tone }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Caja #{openRegister.registerNumber} abierta
            </Typography>
            <Chip size="small" label="En turno" sx={{ bgcolor: alpha(tone, 0.12), color: tone, fontWeight: 700 }} />
          </Stack>

          {(openRegister.openingFloat || []).length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
                FONDO CON EL QUE ABRIÓ
              </Typography>
              <Stack sx={{ mt: 1, maxWidth: 360 }} divider={<Divider flexItem />}>
                {openRegister.openingFloat.map((row) => (
                  <Stack key={row.currency} direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                    <Typography variant="body2">{row.currency}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={numeric}>
                      {money(row.amount, row.currency)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, maxWidth: '62ch' }}>
            Para abrir una caja nueva primero hay que cerrar esta desde Cierre de Caja.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // ── Formulario de apertura ──────────────────────────────────────────
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <PointOfSaleIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Abrir caja
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: '62ch' }}>
          Contá el efectivo que hay en la gaveta antes de empezar el turno y declaralo acá. Es contra ese monto que se
          va a cuadrar la caja al cerrar.
        </Typography>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
          FONDO DE APERTURA
        </Typography>

        <Stack spacing={2} sx={{ mt: 1.5, maxWidth: 460 }}>
          {currencies.map((option) => (
            <Box key={option.currency}>
              <TextField
                fullWidth
                size="small"
                type="number"
                required
                label={`Efectivo en ${option.currency}`}
                value={amounts[option.currency] ?? ''}
                onChange={(e) => setAmounts({ ...amounts, [option.currency]: e.target.value })}
                inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                helperText={
                  option.isBase
                    ? 'Moneda base del hotel'
                    : `Tasa ${Number(option.rate).toFixed(4)} · equivale a ${money(
                        (Number(amounts[option.currency]) || 0) / (option.rate || 1),
                        baseCurrency
                      )}`
                }
              />
            </Box>
          ))}
        </Stack>

        {currencies.length > 1 && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mt: 2.5, maxWidth: 460, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="body2" fontWeight={700}>
              Total del fondo ({baseCurrency})
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={numeric}>
              {money(totalInBase, baseCurrency)}
            </Typography>
          </Stack>
        )}

        {!allDeclared && (
          <Alert severity="info" sx={{ mt: 2.5, maxWidth: 460 }}>
            Declará el monto de cada moneda, aunque sea cero. Un cero contado no es lo mismo que un campo en blanco.
          </Alert>
        )}

        <Button
          variant="contained"
          disableElevation
          size="large"
          sx={{ mt: 2.5, maxWidth: 460 }}
          fullWidth
          disabled={!allDeclared || loading || currencies.length === 0}
          onClick={handleOpen}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PointOfSaleIcon />}
        >
          {loading ? 'Abriendo…' : 'Abrir caja'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default OpenCashRegister;
